/**
 * PATH: packages/email-hub/src/feature/hooks/useEmailTags.ts
 * FILE_ID: EMAILHUB-FEATURE-USE-EMAIL-TAGS-001
 * PURPOSE: Shared org tag-catalog hook (react-query) + assign/remove helpers.
 * WHY: Per-mount useState fetch of /api/emails/tags/ made every reader open
 *      re-hit the catalog (BL-3 / #27). One query key per session shares the
 *      catalog across EmailTagChips, FilterSidebar, and TagsRulesPage.
 * FLOW: useQuery(['email-tags']) → create/update/delete mutate cache →
 *       assign/remove write email M2M without catalog refetch.
 * RELATED: EmailTagChips.tsx, useEmails.ts (re-export).
 */
import { useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { emailApi } from '../lib/api'

export type EmailTagCatalogRow = { id: number; name: string; color: string }

export const EMAIL_TAGS_QUERY_KEY = ['email-tags'] as const

async function fetchEmailTagCatalog(): Promise<EmailTagCatalogRow[]> {
  const data = await emailApi<EmailTagCatalogRow[] | { results: EmailTagCatalogRow[] }>(
    '/api/emails/tags/',
  )
  return Array.isArray(data) ? data : data.results || []
}

export function useEmailTags() {
  const queryClient = useQueryClient()
  const { data: tags = [], isLoading: loading, refetch } = useQuery({
    queryKey: EMAIL_TAGS_QUERY_KEY,
    queryFn: fetchEmailTagCatalog,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  })

  const fetchTags = useCallback(async () => {
    await refetch()
  }, [refetch])

  const createTag = useCallback(
    async (name: string, color: string) => {
      const tag = await emailApi<EmailTagCatalogRow>('/api/emails/tags/', {
        method: 'POST',
        body: JSON.stringify({ name, color }),
      })
      queryClient.setQueryData<EmailTagCatalogRow[]>(EMAIL_TAGS_QUERY_KEY, (prev) => [
        ...(prev ?? []),
        tag,
      ])
      return tag
    },
    [queryClient],
  )

  const updateTag = useCallback(
    async (tagId: number, patch: { name?: string; color?: string }) => {
      const prev =
        queryClient.getQueryData<EmailTagCatalogRow[]>(EMAIL_TAGS_QUERY_KEY) ?? []
      queryClient.setQueryData<EmailTagCatalogRow[]>(
        EMAIL_TAGS_QUERY_KEY,
        prev.map((t) => (t.id === tagId ? { ...t, ...patch } : t)),
      )
      try {
        const updated = await emailApi<EmailTagCatalogRow>(
          `/api/emails/tags/${tagId}/`,
          { method: 'PATCH', body: JSON.stringify(patch) },
        )
        queryClient.setQueryData<EmailTagCatalogRow[]>(
          EMAIL_TAGS_QUERY_KEY,
          (curr) => (curr ?? []).map((t) => (t.id === tagId ? updated : t)),
        )
        return updated
      } catch (err) {
        queryClient.setQueryData(EMAIL_TAGS_QUERY_KEY, prev)
        throw err
      }
    },
    [queryClient],
  )

  const deleteTag = useCallback(
    async (tagId: number) => {
      const prev =
        queryClient.getQueryData<EmailTagCatalogRow[]>(EMAIL_TAGS_QUERY_KEY) ?? []
      queryClient.setQueryData<EmailTagCatalogRow[]>(
        EMAIL_TAGS_QUERY_KEY,
        prev.filter((t) => t.id !== tagId),
      )
      try {
        await emailApi(`/api/emails/tags/${tagId}/`, { method: 'DELETE' })
      } catch (err) {
        queryClient.setQueryData(EMAIL_TAGS_QUERY_KEY, prev)
        throw err
      }
    },
    [queryClient],
  )

  const assignTag = useCallback(async (emailId: string, tagId: number) => {
    await emailApi(`/api/emails/${emailId}/tags/`, {
      method: 'POST',
      body: JSON.stringify({ tag_id: tagId }),
    })
  }, [])

  const removeTag = useCallback(async (emailId: string, tagId: number) => {
    await emailApi(`/api/emails/${emailId}/tags/${tagId}/`, { method: 'DELETE' })
  }, [])

  return {
    tags,
    loading,
    fetchTags,
    createTag,
    updateTag,
    deleteTag,
    assignTag,
    removeTag,
  }
}
